import { useState, useEffect, useCallback, useRef, useId } from 'react';
import { api } from '../lib/auth';
import { ModalShell, Button, PageHeader, Stat as StatTile } from '../lib/ui';
import {
  sizeProfileFor, linePriceForGrams, parseGrams, matchProduct, roundPrice, summarise,
} from '../lib/subscriptionPricing';

// ── Helpers ───────────────────────────────────────────────────────────────────

function statusColor(s) {
  return { pending: '#d97706', active: '#16a34a', paused: '#4f46e5', cancelled: '#9ca3af' }[s] || '#9ca3af';
}

function statusBg(s) {
  return { pending: '#fef3c7', active: '#dcfce7', paused: '#e0e7ff', cancelled: '#f3f4f6' }[s] || '#f3f4f6';
}

function freqLabel(f) {
  return { weekly: 'Weekly', fortnightly: 'Fortnightly', monthly: 'Monthly' }[f] || f;
}

// Deliveries per month by frequency — mirrors the server's MRR maths in
// garageFarmServer routes/subscriptionAdmin.js (/analytics).
const MONTHLY_MULTIPLIER = { weekly: 4.33, fortnightly: 2.17, monthly: 1 };

// What this subscription is worth to us per month (price per delivery ×
// deliveries per month). Returns null if no price is set yet.
function monthlyValueOf(sub) {
  if (sub?.pricePerDelivery == null) return null;
  return sub.pricePerDelivery * (MONTHLY_MULTIPLIER[sub.frequency] ?? 1);
}

// Total currently accrued but not yet billed (monthly-billing subs).
function pendingTotalOf(sub) {
  return (sub?.pendingCharges || []).reduce((s, c) => s + Number(c.amount || 0), 0);
}

function fmt(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtWhen(d) {
  if (!d) return '-';
  return new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// One entry of sub.statusHistory (server: SubscriptionPlan.statusHistory),
// e.g. "Paused by the customer".
function statusChangeLabel(h) {
  let what;
  if (h.to === 'active') what = h.from === 'paused' ? 'Resumed' : 'Activated';
  else if (h.to === 'pending') what = h.from ? 'Edited, back to review' : 'Signed up';
  else what = { paused: 'Paused', cancelled: 'Cancelled' }[h.to] || h.to;
  return `${what} ${h.by === 'customer' ? 'by the customer' : 'from the dashboard'}`;
}

function fmtShort(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

// Saved-card indicator. `status` is an entry from GET subscriptions/card-status:
// { hasCard, brand, last4, unknown }. `undefined` = still loading (render nothing).
function CardBadge({ status, showDetail = false }) {
  if (!status) return null;
  const base = {
    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
    display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
  };
  if (status.unknown) {
    return <span style={{ ...base, background: '#f3f4f6', color: '#6b7280' }} title="Couldn't reach Stripe">💳 Card ?</span>;
  }
  if (status.hasCard) {
    const label = showDetail && status.brand
      ? `${status.brand} ••••${status.last4}`
      : 'Card on file';
    return <span style={{ ...base, background: '#dcfce7', color: '#15803d' }}>💳 {label}</span>;
  }
  return <span style={{ ...base, background: '#fee2e2', color: '#b91c1c' }} title="No saved card — charges fall back to an emailed invoice">💳 No card</span>;
}

// ── Stat card ─────────────────────────────────────────────────────────────────

function Stat({ label, value, sub, color }) {
  return <StatTile label={label} value={value} sub={sub} accent={color} valueColor={color} />;
}

// ── Subscription detail panel ─────────────────────────────────────────────────

function SubDetail({ sub, cardStatus, onUpdate }) {
  const [editing, setEditing]     = useState(false);
  const [price, setPrice]         = useState(sub.pricePerDelivery ?? '');
  const [adminNotes, setNotes]    = useState(sub.adminNotes || '');
  const [nextDate, setNextDate]   = useState(sub.nextDeliveryDate ? sub.nextDeliveryDate.slice(0, 10) : '');
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState('');
  const [billingMode, setBillingMode] = useState(sub.billingMode || 'per_delivery');
  const [chargeAmount, setChargeAmount] = useState(sub.pricePerDelivery ?? '');
  const [chargeDesc, setChargeDesc]     = useState('');
  const [showCharge, setShowCharge]     = useState(false);
  const [chargeMsg, setChargeMsg]       = useState('');
  const [logAmount, setLogAmount]       = useState(sub.pricePerDelivery ?? '');
  const [logDesc, setLogDesc]           = useState('');
  const [showLog, setShowLog]           = useState(false);
  const [boxType, setBoxType]           = useState(sub.type || 'surprise');
  const [items, setItems]               = useState(sub.items || []);
  const [preferences, setPreferences]   = useState(sub.preferences || '');

  // Re-sync the contents fields from the saved sub whenever the form opens, so
  // a cancelled edit doesn't leave stale rows behind.
  const startEdit = () => {
    setBoxType(sub.type || 'surprise');
    setItems((sub.items || []).map(i => ({ ...i })));
    setPreferences(sub.preferences || '');
    setEditing(true);
  };

  const pending      = sub.pendingCharges || [];
  const pendingTotal = pending.reduce((s, c) => s + Number(c.amount || 0), 0);

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      await api.patch(`subscriptions/${sub._id}`, {
        pricePerDelivery: price !== '' ? Number(price) : null,
        adminNotes,
        nextDeliveryDate: nextDate || null,
        billingMode,
        type: boxType,
        items: boxType === 'custom' ? items : [],
        preferences,
      });
      setMsg('Saved');
      setEditing(false);
      onUpdate();
    } catch { setMsg('Failed to save'); }
    finally { setSaving(false); }
  };

  const activate = async () => {
    if (!price) { alert('Set a price per delivery before activating.'); return; }
    setSaving(true);
    try {
      await api.post(`subscriptions/${sub._id}/activate`, {
        pricePerDelivery: Number(price),
        nextDeliveryDate: nextDate || undefined,
      });
      onUpdate();
    } catch { alert('Failed to activate'); }
    finally { setSaving(false); }
  };

  const pause  = async () => {
    setSaving(true);
    try { await api.post(`subscriptions/${sub._id}/pause`); onUpdate(); }
    catch { alert('Failed to pause'); }
    finally { setSaving(false); }
  };

  const resume = async () => {
    setSaving(true);
    try { await api.patch(`subscriptions/${sub._id}`, { status: 'active' }); onUpdate(); }
    catch { alert('Failed to resume'); }
    finally { setSaving(false); }
  };

  const cancel = async () => {
    if (!window.confirm('Cancel this subscription?')) return;
    setSaving(true);
    try { await api.post(`subscriptions/${sub._id}/cancel`); onUpdate(); }
    catch { alert('Failed to cancel'); }
    finally { setSaving(false); }
  };

  const resendActivation = async () => {
    setSaving(true);
    try {
      const r = await api.post(`subscriptions/${sub._id}/resend-activation`);
      alert(r.data.message);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to resend email');
    } finally {
      setSaving(false);
    }
  };

  const advanceDelivery = async () => {
    setSaving(true);
    try { await api.post(`subscriptions/${sub._id}/advance-delivery`); onUpdate(); }
    catch { alert('Failed to advance delivery'); }
    finally { setSaving(false); }
  };

  const logDelivery = async () => {
    const amt = logAmount === '' ? sub.pricePerDelivery : Number(logAmount);
    if (!amt || isNaN(Number(amt)) || Number(amt) <= 0) {
      setChargeMsg('Enter a valid amount (or set a price per delivery)'); return;
    }
    setSaving(true); setChargeMsg('');
    try {
      await api.post(`subscriptions/${sub._id}/log-delivery`, {
        amount: Number(amt),
        description: logDesc || undefined,
      });
      setChargeMsg(`✓ Delivery logged - £${Number(amt).toFixed(2)} unbilled`);
      setShowLog(false); setLogDesc('');
      onUpdate();
    } catch (e) {
      setChargeMsg(e.response?.data?.error || 'Failed to log delivery');
    } finally { setSaving(false); }
  };

  const billMonth = async () => {
    if (!pending.length) return;
    if (!window.confirm(`Bill ${sub.customerId?.name || 'this customer'} £${pendingTotal.toFixed(2)} for ${pending.length} ${pending.length === 1 ? 'delivery' : 'deliveries'}?`)) return;
    setSaving(true); setChargeMsg('');
    try {
      const r = await api.post(`subscriptions/${sub._id}/bill-month`);
      setChargeMsg(`✓ ${r.data.message} - £${r.data.amount.toFixed(2)}`);
      onUpdate();
    } catch (e) {
      setChargeMsg(e.response?.data?.error || 'Failed to bill month');
    } finally { setSaving(false); }
  };

  const sendCharge = async () => {
    if (!chargeAmount || isNaN(Number(chargeAmount)) || Number(chargeAmount) <= 0) {
      setChargeMsg('Enter a valid amount'); return;
    }
    setSaving(true); setChargeMsg('');
    try {
      const r = await api.post(`subscriptions/${sub._id}/charge`, {
        amount:        Number(chargeAmount),
        description:   chargeDesc || undefined,
        advanceAfter:  true,
      });
      setChargeMsg(`✓ Invoice sent - £${r.data.amount.toFixed(2)}`);
      setShowCharge(false);
      onUpdate();
    } catch (e) {
      setChargeMsg(e.response?.data?.error || 'Failed to send invoice');
    } finally {
      setSaving(false);
    }
  };

  const c = sub.customerId;

  return (
    <div style={{ padding: 20, background: 'var(--bg)', borderRadius: 'var(--r-md)', marginTop: 8 }}>
      {/* Customer info */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>{c?.name || '-'}</span>
          <CardBadge status={cardStatus} showDetail />
        </div>
        <div style={{ color: 'var(--muted)', fontSize: 13 }}>{c?.email}</div>
        {c?.phone && <div style={{ color: 'var(--muted)', fontSize: 13 }}>{c.phone}</div>}
        {cardStatus && !cardStatus.hasCard && !cardStatus.unknown && (
          <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 4 }}>
            No saved card — “Charge customer” will email a Stripe invoice instead of charging automatically.
          </div>
        )}
      </div>

      {/* What they want */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 6 }}>
          {sub.type === 'surprise' ? 'Surprise - Preferences' : 'Custom Items'}
        </div>
        {sub.type === 'surprise' ? (
          <p style={{ fontSize: 13, margin: 0 }}>{sub.preferences || 'No preferences set'}</p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
            {sub.items?.map((it, i) => (
              <li key={i}>{it.name}{it.quantity ? ` - ${it.quantity}` : ''}{it.notes ? ` (${it.notes})` : ''}</li>
            ))}
          </ul>
        )}
      </div>

      {/* Delivery */}
      {sub.deliveryAddress?.line1 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 4 }}>Delivery</div>
          <p style={{ fontSize: 13, margin: 0 }}>
            {[sub.deliveryAddress.line1, sub.deliveryAddress.line2, sub.deliveryAddress.city, sub.deliveryAddress.postcode].filter(Boolean).join(', ')}
          </p>
          {sub.deliveryInstructions && <p style={{ fontSize: 12, color: 'var(--muted)', margin: '2px 0 0' }}>{sub.deliveryInstructions}</p>}
          {sub.deliverySlot && <p style={{ fontSize: 12, color: 'var(--muted)', margin: '2px 0 0' }}>Slot: {sub.deliverySlot}</p>}
        </div>
      )}

      {/* Who paused / resumed / cancelled, newest first */}
      {sub.statusHistory?.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 4 }}>Status history</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
            {[...sub.statusHistory].reverse().map((h, i) => (
              <li key={i}>
                <span style={{ color: statusColor(h.to), fontWeight: 600 }}>{statusChangeLabel(h)}</span>
                <span style={{ color: 'var(--muted)' }}> · {fmtWhen(h.at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Admin editable fields */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 6 }}>Admin Controls</div>
        <div style={{ marginBottom: 8 }}>
          <PriceCalculator
            sub={sub}
            key={JSON.stringify(sub.items || [])}
            onUse={(v) => { if (!editing) startEdit(); setPrice(String(v)); setMsg(''); }}
          />
        </div>
        {editing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <ContentsEditor
              boxType={boxType} setBoxType={setBoxType}
              items={items} setItems={setItems}
              preferences={preferences} setPreferences={setPreferences}
            />
            <label style={{ fontSize: 12, fontWeight: 600 }}>
              Price per delivery (£)
              <input type="number" min="0" step="0.01" value={price} onChange={e => setPrice(e.target.value)}
                style={{ display: 'block', width: '100%', marginTop: 3, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', fontSize: 13, background: 'var(--panel)' }} />
            </label>
            <label style={{ fontSize: 12, fontWeight: 600 }}>
              Billing
              <select value={billingMode} onChange={e => setBillingMode(e.target.value)}
                style={{ display: 'block', width: '100%', marginTop: 3, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', fontSize: 13, background: 'var(--panel)' }}>
                <option value="per_delivery">Per delivery — charge before each delivery</option>
                <option value="monthly">Monthly — log deliveries, bill once a month</option>
              </select>
            </label>
            <label style={{ fontSize: 12, fontWeight: 600 }}>
              Next delivery date
              <input type="date" value={nextDate} onChange={e => setNextDate(e.target.value)}
                style={{ display: 'block', width: '100%', marginTop: 3, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', fontSize: 13, background: 'var(--panel)' }} />
            </label>
            <label style={{ fontSize: 12, fontWeight: 600 }}>
              Admin notes (internal)
              <textarea value={adminNotes} onChange={e => setNotes(e.target.value)}
                rows={2} style={{ display: 'block', width: '100%', marginTop: 3, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', fontSize: 13, resize: 'vertical', background: 'var(--panel)' }} />
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={save} disabled={saving} style={btnStyle('#113449', '#fff')}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={() => setEditing(false)} style={btnStyle('var(--panel-2)', 'var(--text)')}>Cancel</button>
            </div>
            {msg && <span style={{ fontSize: 12, color: msg === 'Saved' ? '#16a34a' : '#b91c1c' }}>{msg}</span>}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontSize: 13 }}>Price: {sub.pricePerDelivery != null ? `£${sub.pricePerDelivery.toFixed(2)}` : <span style={{ color: 'var(--muted)' }}>not set</span>}{sub.billingMode === 'monthly' ? ' / delivery' : ''}</div>
            <div style={{ fontSize: 13 }}>Billing: <strong>{sub.billingMode === 'monthly' ? 'Monthly (bill once a month)' : 'Per delivery'}</strong></div>
            <div style={{ fontSize: 13 }}>Next delivery: <strong>{fmtShort(sub.nextDeliveryDate)}</strong></div>
            {sub.adminNotes && <div style={{ fontSize: 12, color: 'var(--muted)', fontStyle: 'italic' }}>{sub.adminNotes}</div>}
            <button onClick={startEdit} style={{ ...btnStyle('var(--panel-2)', 'var(--text)'), marginTop: 4, width: 'fit-content' }}>Edit</button>
          </div>
        )}
      </div>

      {/* Monthly billing panel */}
      {sub.status === 'active' && sub.billingMode === 'monthly' && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginBottom: 4, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)' }}>
            Unbilled this month: £{pendingTotal.toFixed(2)} · {pending.length} {pending.length === 1 ? 'delivery' : 'deliveries'}
          </div>
          {pending.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: 'var(--muted)' }}>
              {pending.map((c, i) => (
                <li key={i}>{fmtShort(c.date)} — £{Number(c.amount).toFixed(2)}{c.description ? ` · ${c.description}` : ''}</li>
              ))}
            </ul>
          )}

          {!showLog ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => { setShowLog(true); setChargeMsg(''); setLogAmount(sub.pricePerDelivery ?? ''); }} style={btnStyle('var(--panel-2)', 'var(--text)')}>
                ＋ Log delivery
              </button>
              <button onClick={billMonth} disabled={saving || !pending.length} style={btnStyle('#113449', '#fff')}>
                💳 Bill this month (£{pendingTotal.toFixed(2)})
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)' }}>Log a delivery (not charged yet)</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input
                  type="number" min="0.01" step="0.01" placeholder="Amount (£)"
                  value={logAmount} onChange={e => setLogAmount(e.target.value)}
                  style={{ padding: '5px 10px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', fontSize: 13, width: 120, background: 'var(--panel)' }}
                />
                <input
                  type="text" placeholder="Description (optional)"
                  value={logDesc} onChange={e => setLogDesc(e.target.value)}
                  style={{ padding: '5px 10px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', fontSize: 13, flex: 1, minWidth: 160, background: 'var(--panel)' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={logDelivery} disabled={saving} style={btnStyle('#113449', '#fff')}>
                  {saving ? 'Saving…' : 'Log delivery'}
                </button>
                <button onClick={() => setShowLog(false)} style={btnStyle('var(--panel-2)', 'var(--text)')}>Cancel</button>
              </div>
            </div>
          )}
          {chargeMsg && (
            <div style={{ fontSize: 12, color: chargeMsg.startsWith('✓') ? '#16a34a' : '#b91c1c' }}>{chargeMsg}</div>
          )}
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>
            Logging a delivery advances the next delivery date but takes no payment. “Bill this month” charges the saved card once for the total above (or emails one Stripe invoice), then clears the list.
          </div>
        </div>
      )}

      {/* Charge panel */}
      {sub.status === 'active' && sub.billingMode !== 'monthly' && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginBottom: 4 }}>
          {!showCharge ? (
            <button onClick={() => setShowCharge(true)} style={btnStyle('#113449', '#fff')}>
              💳 Charge customer
            </button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)' }}>Send Stripe invoice</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input
                  type="number" min="0.01" step="0.01" placeholder="Amount (£)"
                  value={chargeAmount} onChange={e => setChargeAmount(e.target.value)}
                  style={{ padding: '5px 10px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', fontSize: 13, width: 120, background: 'var(--panel)' }}
                />
                <input
                  type="text" placeholder="Description (optional)"
                  value={chargeDesc} onChange={e => setChargeDesc(e.target.value)}
                  style={{ padding: '5px 10px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', fontSize: 13, flex: 1, minWidth: 160, background: 'var(--panel)' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={sendCharge} disabled={saving} style={btnStyle('#113449', '#fff')}>
                  {saving ? 'Sending…' : 'Send invoice'}
                </button>
                <button onClick={() => setShowCharge(false)} style={btnStyle('var(--panel-2)', 'var(--text)')}>Cancel</button>
              </div>
              {chargeMsg && (
                <div style={{ fontSize: 12, color: chargeMsg.startsWith('✓') ? '#16a34a' : '#b91c1c' }}>{chargeMsg}</div>
              )}
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                If the customer has a saved card it will be charged immediately and they'll get a receipt. Otherwise a Stripe invoice is emailed to them. Delivery date auto-advances either way.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      {sub.status !== 'cancelled' && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          {sub.status === 'pending' && (
            <button onClick={activate} disabled={saving} style={btnStyle('#16a34a', '#fff')}>✓ Activate</button>
          )}
          {sub.status === 'active' && (
            <>
              {sub.billingMode !== 'monthly' && (
                <button onClick={advanceDelivery} disabled={saving} style={btnStyle('#4f46e5', '#fff')}>↻ Next delivery</button>
              )}
              <button onClick={pause} disabled={saving} style={btnStyle('var(--panel-2)', 'var(--text)')}>⏸ Pause</button>
              <button onClick={resendActivation} disabled={saving} style={btnStyle('var(--panel-2)', 'var(--text)')}>✉ Resend confirmation</button>
            </>
          )}
          {sub.status === 'paused' && (
            <button onClick={resume} disabled={saving} style={btnStyle('#16a34a', '#fff')}>▶ Resume</button>
          )}
          <button onClick={cancel} disabled={saving} style={btnStyle('#fee2e2', '#b91c1c')}>✕ Cancel</button>
        </div>
      )}
    </div>
  );
}

function btnStyle(bg, color) {
  return {
    background: bg, color, border: 'none', borderRadius: 'var(--r-md)', padding: '6px 14px',
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
  };
}

// ── Box contents editor ──────────────────────────────────────────────────────

// Lets the admin switch between custom/surprise and add, edit or remove the
// items in a custom box. Product names autocomplete from the catalogue and the
// quantity suggests that product's pack sizes, but both stay free text to match
// what customers can enter ("1 bunch", "x2").
function ContentsEditor({ boxType, setBoxType, items, setItems, preferences, setPreferences }) {
  const products = useCatalogue();
  const uid = useId();
  const patchItem = (i, next) => setItems(its => its.map((it, j) => j === i ? { ...it, ...next } : it));
  const removeItem = (i) => setItems(its => its.filter((_, j) => j !== i));
  const addItem = () => setItems(its => [...its, { name: '', quantity: '', notes: '' }]);
  const move = (i, dir) => setItems(its => {
    const j = i + dir;
    if (j < 0 || j >= its.length) return its;
    const next = [...its];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 10, background: 'var(--panel)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 700 }}>Box contents</span>
        {['custom', 'surprise'].map(t => (
          <label key={t} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input type="radio" name={`boxType-${uid}`} checked={boxType === t} onChange={() => setBoxType(t)} />
            {t === 'custom' ? 'Custom items' : 'Surprise'}
          </label>
        ))}
      </div>

      {boxType === 'custom' && (
        <>
          <datalist id="sub-product-names">
            {products.map(p => <option key={p.id} value={p.name} />)}
          </datalist>
          {items.length === 0 && <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>No items yet.</div>}
          {items.map((it, i) => {
            const product = matchProduct(it.name, products);
            const sizes = product ? sizeProfileFor(product) : [];
            return (
              <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', padding: '4px 0', borderBottom: '1px solid var(--border)' }}>
                <input type="text" list="sub-product-names" placeholder="Product" value={it.name}
                  onChange={e => patchItem(i, { name: e.target.value })}
                  style={{ ...calcInput, flex: 2, minWidth: 140 }} />
                <input type="text" list={`sub-sizes-${uid}-${i}`} placeholder="e.g. 100g" value={it.quantity}
                  onChange={e => patchItem(i, { quantity: e.target.value })}
                  style={{ ...calcInput, width: 90 }} />
                <datalist id={`sub-sizes-${uid}-${i}`}>{sizes.map(g => <option key={g} value={`${g}g`} />)}</datalist>
                <input type="text" placeholder="Notes (optional)" value={it.notes || ''}
                  onChange={e => patchItem(i, { notes: e.target.value })}
                  style={{ ...calcInput, flex: 1, minWidth: 110 }} />
                <button onClick={() => move(i, -1)} disabled={i === 0} title="Move up"
                  style={{ ...btnStyle('transparent', 'var(--muted)'), padding: 2 }}>↑</button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1} title="Move down"
                  style={{ ...btnStyle('transparent', 'var(--muted)'), padding: 2 }}>↓</button>
                <button onClick={() => removeItem(i)} title="Remove"
                  style={{ ...btnStyle('transparent', '#b91c1c'), padding: 2 }}>✕</button>
              </div>
            );
          })}
          <button onClick={addItem} style={{ ...btnStyle('var(--panel-2)', 'var(--text)'), marginTop: 8, fontSize: 11 }}>+ Add product</button>
        </>
      )}

      <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginTop: 10 }}>
        {boxType === 'surprise' ? 'Preferences' : 'General notes / preferences'}
        <textarea value={preferences} onChange={e => setPreferences(e.target.value)} rows={2}
          style={{ display: 'block', width: '100%', marginTop: 3, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', fontSize: 13, resize: 'vertical', background: 'var(--panel)' }} />
      </label>
      {boxType === 'custom' && items.some(it => !it.name.trim()) && (
        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Rows without a product name are dropped on save.</div>
      )}
    </div>
  );
}

// ── Price calculator ─────────────────────────────────────────────────────────

// Catalogue is small and rarely changes; cache it across panel opens.
let _productCache = null;
let _productPromise = null;

function useCatalogue() {
  const [products, setProducts] = useState(_productCache || []);
  useEffect(() => {
    if (_productCache) return;
    if (!_productPromise) {
      _productPromise = api.get('/products')
        .then(r => { _productCache = Array.isArray(r.data) ? r.data : []; return _productCache; })
        .catch(() => { _productPromise = null; return []; });
    }
    let live = true;
    _productPromise.then(list => { if (live) setProducts(list); });
    return () => { live = false; };
  }, []);
  return products;
}

const calcInput = {
  padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
  fontSize: 13, background: 'var(--panel)',
};

function makeRow(products, { name = '', quantity = '', notes = '' } = {}) {
  const product = matchProduct(name, products);
  const parsed = parseGrams(quantity);
  const grams = parsed != null ? parsed : (product ? sizeProfileFor(product)[0] : 50);
  return {
    key: Math.random().toString(36).slice(2),
    label: [name, quantity].filter(Boolean).join(' · '),
    productId: product ? String(product.id) : '',
    grams,
    qty: 1,
    include: true,
    manualPrice: parsed == null && !!quantity,   // non-weight qty ("1 bunch") -> admin sets price
    price: '',
  };
}

function PriceCalculator({ sub, onUse }) {
  const products = useCatalogue();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [discountPct, setDiscountPct] = useState(0);
  const [deliveryFee, setDeliveryFee] = useState(sub.deliverySlot === 'late' ? 1 : 0);
  const seeded = useRef(false);

  // Seed rows from the customer's items once the catalogue is in.
  useEffect(() => {
    if (seeded.current || !open) return;
    if (products.length === 0) return;
    seeded.current = true;
    const seed = sub.type === 'custom' && sub.items?.length
      ? sub.items.map(it => makeRow(products, it))
      : [makeRow(products, {})];
    setRows(seed);
  }, [open, products, sub]);

  const byId = (id) => products.find(p => String(p.id) === String(id));

  const computedPrice = (row) => {
    if (row.manualPrice) return Number(row.price);
    const unit = linePriceForGrams(byId(row.productId), row.grams);
    return unit == null ? null : unit * (Number(row.qty) || 1);
  };

  const patch = (key, next) => setRows(rs => rs.map(r => r.key === key ? { ...r, ...next } : r));
  const addRow = () => setRows(rs => [...rs, makeRow(products, {})]);
  const removeRow = (key) => setRows(rs => rs.filter(r => r.key !== key));

  const priced = rows.map(r => ({ include: r.include, price: computedPrice(r) }));
  const sum = summarise(priced, { discountPct, deliveryFee });
  const finalPrice = roundPrice(sum.total);

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} style={{ ...btnStyle('var(--panel-2)', 'var(--text)'), width: 'fit-content' }}>
        🧮 Price calculator
      </button>
    );
  }

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 10, background: 'var(--panel)', marginTop: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700 }}>Price calculator</span>
        <button onClick={() => setOpen(false)} style={{ ...btnStyle('transparent', 'var(--muted)'), padding: 2 }}>✕</button>
      </div>

      {products.length === 0 && <div style={{ fontSize: 12, color: 'var(--muted)' }}>Loading catalogue…</div>}

      {rows.map(row => {
        const p = byId(row.productId);
        const sizes = p ? sizeProfileFor(p) : [];
        const line = computedPrice(row);
        return (
          <div key={row.key} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
            <input type="checkbox" checked={row.include} onChange={e => patch(row.key, { include: e.target.checked })} />
            <div style={{ flex: 1, minWidth: 140 }}>
              {row.label && <div style={{ fontSize: 11, color: 'var(--muted)' }}>“{row.label}”</div>}
              <select value={row.productId} onChange={e => {
                const np = byId(e.target.value);
                patch(row.key, { productId: e.target.value, manualPrice: false, grams: np ? (sizeProfileFor(np).includes(row.grams) ? row.grams : sizeProfileFor(np)[0]) : row.grams });
              }} style={{ ...calcInput, width: '100%' }}>
                <option value="">— pick product —</option>
                {products.map(pr => <option key={pr.id} value={pr.id}>{pr.name}</option>)}
              </select>
            </div>
            {!row.manualPrice && (
              <>
                <input type="number" min="1" value={row.grams} onChange={e => patch(row.key, { grams: Number(e.target.value) })}
                  list={`sizes-${row.key}`} style={{ ...calcInput, width: 78 }} title="grams" />
                <datalist id={`sizes-${row.key}`}>{sizes.map(s => <option key={s} value={s} />)}</datalist>
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>g ×</span>
                <input type="number" min="1" value={row.qty} onChange={e => patch(row.key, { qty: Number(e.target.value) })}
                  style={{ ...calcInput, width: 60 }} title="quantity" />
              </>
            )}
            {row.manualPrice && (
              <input type="number" min="0" step="0.01" placeholder="£ manual" value={row.price}
                onChange={e => patch(row.key, { price: e.target.value })} style={{ ...calcInput, width: 92 }} />
            )}
            <button onClick={() => patch(row.key, { manualPrice: !row.manualPrice })}
              style={{ ...btnStyle('transparent', 'var(--muted)'), padding: '2px 4px', fontSize: 11 }}
              title="toggle manual price">{row.manualPrice ? '↩︎' : '✎£'}</button>
            <span style={{ fontSize: 12, fontWeight: 600, width: 52, textAlign: 'right' }}>
              {line == null ? '—' : `£${line.toFixed(2)}`}
            </span>
            <button onClick={() => removeRow(row.key)} style={{ ...btnStyle('transparent', 'var(--muted)'), padding: 2 }}>✕</button>
          </div>
        );
      })}

      <button onClick={addRow} style={{ ...btnStyle('var(--panel-2)', 'var(--text)'), marginTop: 8, fontSize: 11 }}>+ Add item</button>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 10, fontSize: 12 }}>
        <label>Discount %{' '}
          <input type="number" min="0" max="100" value={discountPct}
            onChange={e => setDiscountPct(Number(e.target.value))} style={{ ...calcInput, width: 64 }} />
        </label>
        <label>Delivery £{' '}
          <input type="number" min="0" step="0.01" value={deliveryFee}
            onChange={e => setDeliveryFee(Number(e.target.value))} style={{ ...calcInput, width: 72 }} />
          {sub.deliverySlot === 'late' && <span style={{ color: 'var(--muted)' }}> (late slot)</span>}
        </label>
      </div>

      <div style={{ marginTop: 10, fontSize: 12, color: 'var(--muted)', lineHeight: 1.7 }}>
        <div>Items: £{sum.itemsTotal.toFixed(2)}</div>
        {sum.discount > 0 && <div>Discount ({discountPct}%): −£{sum.discount.toFixed(2)}</div>}
        {sum.deliveryFee > 0 && <div>Delivery: £{sum.deliveryFee.toFixed(2)}</div>}
        {sum.unpriced > 0 && <div style={{ color: '#d97706' }}>{sum.unpriced} item(s) not priced yet</div>}
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 20 }}>£{finalPrice.toFixed(2)}</div>
        <button onClick={() => onUse(Number(finalPrice.toFixed(2)))} disabled={finalPrice <= 0}
          style={btnStyle('#16a34a', '#fff')}>Use this price</button>
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>per delivery · rounded to 5p</span>
      </div>
    </div>
  );
}


// ── Charge everyone in one go ────────────────────────────────────────────────
// The per-subscription "Charge customer" button, done for the whole book at
// once. Nothing is taken until the preview has been seen and confirmed: the
// ids shown are the ids sent, so a subscription that changes in between is
// left out rather than charged on stale figures. Each customer gets one email
// listing every subscription charged and what it covered - the server sends
// those; Stripe still sends its own receipt or invoice per charge.

function ChargeAllButton({ cardStatus, onDone }) {
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [skipped, setSkipped] = useState({});   // subId -> true when unticked
  const [running, setRunning] = useState(false);
  const [result, setResult]   = useState(null);
  const [err, setErr]         = useState('');

  const openPreview = async () => {
    setOpen(true); setLoading(true); setErr(''); setResult(null); setSkipped({});
    try {
      const r = await api.get('subscriptions/charge-all/preview');
      setPreview(r.data);
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not work out what is due');
    } finally { setLoading(false); }
  };

  const close = () => {
    setOpen(false); setPreview(null); setResult(null); setErr('');
    if (result) onDone();
  };

  const chosen = (preview?.subscriptions || []).filter(x => !skipped[x._id]);
  const total  = chosen.reduce((t, x) => t + x.amount, 0);

  const run = async () => {
    if (!chosen.length) return;
    if (!window.confirm(`Charge ${chosen.length} ${chosen.length === 1 ? 'subscription' : 'subscriptions'} for £${total.toFixed(2)} now? Cards on file are charged straight away.`)) return;
    setRunning(true); setErr('');
    try {
      const r = await api.post('subscriptions/charge-all', { ids: chosen.map(x => x._id) });
      setResult(r.data);
      onDone();
    } catch (e) {
      setErr(e.response?.data?.error || 'Bulk charge failed');
    } finally { setRunning(false); }
  };

  return (
    <>
      <Button variant="success" onClick={openPreview}>💷 Charge all due</Button>

      {open && (
        <ModalShell onClose={close} width={620} ariaLabel="Charge all subscriptions">
          <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: 19, margin: '0 0 4px' }}>
            {result ? 'Charge run finished' : 'Charge all due subscriptions'}
          </h2>
          <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 14px' }}>
            {result
              ? 'Every customer charged has been emailed a breakdown of what it covered.'
              : 'Per-delivery subscriptions are charged their price; monthly ones are charged whatever has been delivered but not yet billed. Untick anything you want to leave out.'}
          </p>

          {err && <div style={{ fontSize: 13, color: '#b91c1c', marginBottom: 10 }}>{err}</div>}
          {loading && <p style={{ fontSize: 13, color: 'var(--muted)' }}>Working out what is due…</p>}

          {/* ── Preview ── */}
          {!result && !loading && preview && (
            <>
              {preview.subscriptions.length === 0 && (
                <p style={{ fontSize: 13, color: 'var(--muted)' }}>Nothing is due right now.</p>
              )}

              {preview.subscriptions.map(x => {
                const card = cardStatus[x.customerId];
                return (
                  <label
                    key={x._id}
                    style={{
                      display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 0',
                      borderBottom: '1px solid var(--border)', cursor: 'pointer',
                      opacity: skipped[x._id] ? 0.45 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!skipped[x._id]}
                      onChange={e => setSkipped(sk => ({ ...sk, [x._id]: !e.target.checked }))}
                      style={{ marginTop: 3, transform: 'scale(1.15)' }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{x.name}</span>
                        <CardBadge status={card} />
                        {x.status !== 'active' && (
                          <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: statusBg(x.status), color: statusColor(x.status) }}>
                            {x.status}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                        {x.label} · {x.kind === 'accrued'
                          ? `${x.deliveries} delivered, not yet billed`
                          : `next delivery${x.nextDeliveryDate ? ` ${fmtShort(x.nextDeliveryDate)}` : ''}`}
                      </div>
                      {x.contents?.length > 0 && (
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{x.contents.join(', ')}</div>
                      )}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>£{x.amount.toFixed(2)}</div>
                  </label>
                );
              })}

              {preview.subscriptions.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 14 }}>
                  <div style={{ fontSize: 13 }}>
                    <strong>{chosen.length}</strong> selected ·{' '}
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: 18 }}>£{total.toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button onClick={close} disabled={running}>Cancel</Button>
                    <Button variant="success" onClick={run} disabled={running || !chosen.length}>
                      {running ? 'Charging…' : `Charge £${total.toFixed(2)}`}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── Result ── */}
          {result && (
            <>
              <div style={{ fontSize: 13, marginBottom: 10 }}>
                Charged <strong>£{result.total.toFixed(2)}</strong> across {result.charged.length}{' '}
                {result.charged.length === 1 ? 'subscription' : 'subscriptions'} ·{' '}
                {result.emailed} {result.emailed === 1 ? 'customer emailed' : 'customers emailed'}
              </div>

              {result.charged.map(c => (
                <div key={c._id} style={{ display: 'flex', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{c.name} · {c.label}</div>
                    <div style={{ color: 'var(--muted)' }}>
                      {c.method === 'card' ? `Card charged${c.card ? ` (${c.card})` : ''}` : 'No card on file — invoice emailed'}
                    </div>
                  </div>
                  <div style={{ fontWeight: 700 }}>£{c.amount.toFixed(2)}</div>
                </div>
              ))}

              {result.failed?.length > 0 && (
                <div style={{ marginTop: 12, padding: 10, borderRadius: 'var(--r-md)', background: '#fef2f2', border: '1px solid #fecaca' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#b91c1c', marginBottom: 6 }}>
                    {result.failed.length} not charged — deal with these one at a time
                  </div>
                  {result.failed.map(f => (
                    <div key={f._id} style={{ fontSize: 12, color: '#b91c1c' }}>
                      {f.name} · {f.label} (£{f.amount.toFixed(2)}) — {f.error}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                <Button variant="primary" onClick={close}>Done</Button>
              </div>
            </>
          )}
        </ModalShell>
      )}
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const TABS = ['Overview', 'Subscriptions', 'Customers'];

export default function SubscribersPage() {
  const [tab, setTab]           = useState('Overview');
  const [analytics, setAnalytics] = useState(null);
  const [subs, setSubs]         = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('all');
  const [search, setSearch]     = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [cardStatus, setCardStatus] = useState({});

  const loadAnalytics = useCallback(async () => {
    try { const r = await api.get('subscriptions/analytics'); setAnalytics(r.data); } catch {}
  }, []);

  const loadCardStatus = useCallback(async () => {
    try { const r = await api.get('subscriptions/card-status'); setCardStatus(r.data || {}); } catch {}
  }, []);

  const loadSubs = useCallback(async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const r = await api.get(`subscriptions${params}`);
      setSubs(r.data);
    } catch {}
  }, [filter]);

  const loadCustomers = useCallback(async () => {
    try { const r = await api.get('subscriptions/customers'); setCustomers(r.data); } catch {}
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadAnalytics(), loadSubs(), loadCustomers(), loadCardStatus()]);
    setLoading(false);
  }, [loadAnalytics, loadSubs, loadCustomers, loadCardStatus]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadSubs(); }, [loadSubs]);

  const filteredSubs = subs.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    const c = s.customerId;
    return (
      (c?.name  || '').toLowerCase().includes(q) ||
      (c?.email || '').toLowerCase().includes(q) ||
      (s.label  || '').toLowerCase().includes(q)
    );
  }).sort((a, b) => {
    if (!a.nextDeliveryDate) return 1;
    if (!b.nextDeliveryDate) return -1;
    return new Date(a.nextDeliveryDate) - new Date(b.nextDeliveryDate);
  });

  const filteredCustomers = customers.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
  });

  const panelStyle = {
    background: 'var(--panel)', border: '1px solid var(--border)',
    borderRadius: 'var(--r-xl)', boxShadow: 'var(--shadow-card)', padding: '14px 16px', marginBottom: 10,
  };

  return (
    <div className="dashboard-container">
      <PageHeader
        title="Subscriptions"
        subtitle="Subscribers, deliveries and billing"
        actions={<ChargeAllButton cardStatus={cardStatus} onDone={loadAll} />}
        tabs={TABS.map(t => ({ key: t, label: t }))}
        activeTab={tab}
        onTabChange={setTab}
      />

      {loading && <p style={{ color: 'var(--muted)', fontSize: 13 }}>Loading…</p>}

      {/* ── OVERVIEW TAB ── */}
      {!loading && tab === 'Overview' && analytics && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
            <Stat label="MRR"       value={`£${analytics.mrr.toFixed(2)}`} sub="based on active subs" color="var(--brand)" />
            <Stat label="Active"    value={analytics.active}    color="#16a34a" />
            <Stat label="Pending"   value={analytics.pending}   color="#d97706" />
            {/* <Stat label="Paused"    value={analytics.paused}    color="#4f46e5" /> */}
            {/* <Stat label="Cancelled" value={analytics.cancelled} color="#9ca3af" /> */}
            {/* <Stat label="New (30d)" value={analytics.recentSignups} /> */}
            <Stat
              label="To bill"
              value={`£${(analytics.outstanding?.total || 0).toFixed(2)}`}
              sub={`${analytics.outstanding?.deliveries || 0} unbilled ${analytics.outstanding?.deliveries === 1 ? 'delivery' : 'deliveries'}`}
              color="#b45309"
            />
          </div>

          {/* Outstanding - delivered but not yet billed */}
          {analytics.outstanding?.subs?.length > 0 && (
            <div style={{ ...panelStyle, marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)' }}>
                  Owed - delivered, not yet billed
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#b45309' }}>£{analytics.outstanding.total.toFixed(2)}</div>
              </div>
              {analytics.outstanding.subs.map(o => (
                <div
                  key={o._id}
                  onClick={() => { setFilter('all'); setSearch(''); setExpandedId(o._id); setTab('Subscriptions'); }}
                  title="Open this subscription to bill it"
                  style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{o.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>{o.label} · {o.deliveries} {o.deliveries === 1 ? 'delivery' : 'deliveries'}</div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>£{o.amount.toFixed(2)}</div>
                </div>
              ))}
            </div>
          )}

          {/* Frequency breakdown */}
          {analytics.byFrequency && Object.keys(analytics.byFrequency).length > 0 && (
            <div style={{ ...panelStyle, marginBottom: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 12 }}>Active by frequency</div>
              <div style={{ display: 'flex', gap: 24 }}>
                {['weekly', 'fortnightly', 'monthly'].map(f => (
                  <div key={f}>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{analytics.byFrequency[f] || 0}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>{freqLabel(f)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upcoming deliveries */}
          <div style={panelStyle}>
            <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 12 }}>
              Upcoming deliveries (next 7 days)
            </div>
            {analytics.upcomingDeliveries?.length === 0 && (
              <p style={{ color: 'var(--muted)', fontSize: 13, margin: 0 }}>None scheduled.</p>
            )}
            {analytics.upcomingDeliveries?.map(sub => (
              <div key={sub._id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 600, fontSize: 13, minWidth: 80, color: 'var(--brand)' }}>{fmtShort(sub.nextDeliveryDate)}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{sub.customerId?.name || '-'}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>{sub.label || (sub.type === 'surprise' ? 'Surprise' : 'Custom')} · {freqLabel(sub.frequency)}</div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {sub.pricePerDelivery != null ? `£${sub.pricePerDelivery.toFixed(2)}` : ''}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── SUBSCRIPTIONS TAB ── */}
      {!loading && tab === 'Subscriptions' && (
        <>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <input
              type="text" placeholder="Search by name or email…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', fontSize: 13, background: 'var(--panel)', flex: 1, minWidth: 200 }}
            />
            <select value={filter} onChange={e => setFilter(e.target.value)}
              style={{ padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', fontSize: 13, background: 'var(--panel)' }}>
              <option value="all">All statuses</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {analytics?.outstanding?.total > 0 && (
            <div style={{ ...panelStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: '#fffbeb', borderColor: '#fde68a' }}>
              <span style={{ fontSize: 13, color: '#92400e' }}>
                Outstanding to bill across all subscriptions: <strong>£{analytics.outstanding.total.toFixed(2)}</strong>
                {' '}· {analytics.outstanding.deliveries} {analytics.outstanding.deliveries === 1 ? 'delivery' : 'deliveries'} from {analytics.outstanding.subs.length} {analytics.outstanding.subs.length === 1 ? 'subscriber' : 'subscribers'}
              </span>
            </div>
          )}

          {filteredSubs.length === 0 && <p style={{ color: 'var(--muted)', fontSize: 13 }}>No subscriptions found.</p>}

          {filteredSubs.map(sub => (
            <div key={sub._id} style={{ ...panelStyle, borderLeft: `3px solid ${statusColor(sub.status)}` }}>
              <div
                style={{ display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer', flexWrap: 'wrap' }}
                onClick={() => setExpandedId(expandedId === sub._id ? null : sub._id)}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>
                      {sub.customerId?.name || sub.customerId?.email || '-'}
                    </span>
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
                      background: statusBg(sub.status), color: statusColor(sub.status),
                    }}>{sub.status}</span>
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>{freqLabel(sub.frequency)}</span>
                    {sub.billingMode === 'monthly' && (
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: 'var(--panel-2)', color: 'var(--muted)' }}>monthly</span>
                    )}
                    {(sub.billingMode === 'monthly' || sub.pendingCharges?.length > 0) && (
                      <span
                        style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: '#fef3c7', color: '#b45309' }}
                        title="Delivered but not yet billed"
                      >
                        owes £{pendingTotalOf(sub).toFixed(2)}
                      </span>
                    )}
                    <CardBadge status={cardStatus[sub.customerId?._id]} />
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                    {sub.label || (sub.type === 'surprise' ? 'Surprise Me' : 'Custom')} · Next delivery {fmt(sub.nextDeliveryDate)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexShrink: 0 }}>
                  {sub.pricePerDelivery != null && (
                    <div style={{ textAlign: 'right', lineHeight: 1.3 }}>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>£{sub.pricePerDelivery.toFixed(2)}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                        £{monthlyValueOf(sub).toFixed(2)}/mo
                      </div>
                    </div>
                  )}
                  <span style={{ color: 'var(--muted)', fontSize: 18 }}>{expandedId === sub._id ? '▲' : '▼'}</span>
                </div>
              </div>
              {expandedId === sub._id && (
                <SubDetail
                  sub={sub}
                  cardStatus={cardStatus[sub.customerId?._id]}
                  onUpdate={() => { loadSubs(); loadAnalytics(); loadCardStatus(); }}
                />
              )}
            </div>
          ))}
        </>
      )}

      {/* ── CUSTOMERS TAB ── */}
      {!loading && tab === 'Customers' && (
        <>
          <div style={{ marginBottom: 16 }}>
            <input
              type="text" placeholder="Search by name or email…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', fontSize: 13, background: 'var(--panel)', width: '100%', maxWidth: 320 }}
            />
          </div>

          <div style={{ ...panelStyle, padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--panel-2)', borderBottom: '1px solid var(--border)' }}>
                  {['Name', 'Email', 'Phone', 'Active subs', 'Total subs', 'Joined'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 20, color: 'var(--muted)', textAlign: 'center' }}>No customers found.</td></tr>
                )}
                {filteredCustomers.map(c => (
                  <tr key={c._id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 600 }}>{c.name || '-'}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--muted)' }}>{c.email}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--muted)' }}>{c.phone || '-'}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ background: '#dcfce7', color: '#16a34a', borderRadius: 99, padding: '2px 8px', fontWeight: 700, fontSize: 12 }}>
                        {c.subscriptionCount?.active || 0}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', color: 'var(--muted)' }}>{c.subscriptionCount?.total || 0}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>{fmt(c.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
